import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  Alert
} from '@mui/material';
import { AccessTime, Warning } from '@mui/icons-material';

interface SessionTimeoutWarningProps {
  open: boolean;
  timeRemaining: number;
  onExtendSession: () => void;
  onLogout: () => void;
}

const SessionTimeoutWarning: React.FC<SessionTimeoutWarningProps> = ({
  open,
  timeRemaining,
  onExtendSession,
  onLogout
}) => {
  const formatTime = (milliseconds: number): string => {
    const totalSeconds = Math.ceil(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progressValue = timeRemaining > 0 ? (timeRemaining / (2 * 60 * 1000)) * 100 : 0;

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        pb: 2,
        borderBottom: '1px solid',
        borderBottomColor: 'divider'
      }}>
        <Warning color="warning" fontSize="large" />
        <Typography variant="h6" component="div">
          Session Timeout Warning
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            Your session will expire due to inactivity. You will be automatically logged out to protect your data.
          </Typography>
        </Alert>
        
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AccessTime color="primary" />
            <Typography variant="h6" color="primary">
              Time Remaining: {formatTime(timeRemaining)}
            </Typography>
          </Box>
          
          <LinearProgress 
            variant="determinate" 
            value={progressValue} 
            sx={{ 
              height: 8, 
              borderRadius: 4,
              backgroundColor: 'rgba(255, 152, 0, 0.2)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: progressValue > 50 ? '#ff9800' : '#f44336',
                borderRadius: 4
              }
            }} 
          />
        </Box>
        
        <Typography variant="body2" color="text.secondary">
          Click "Stay Logged In" to extend your session, or "Logout Now" to sign out immediately.
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={onLogout}
          variant="outlined"
          color="secondary"
          sx={{ mr: 1 }}
        >
          Logout Now
        </Button>
        <Button
          onClick={onExtendSession}
          variant="contained"
          color="primary"
          autoFocus
          sx={{
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            '&:hover': {
              background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)'
            }
          }}
        >
          Stay Logged In
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeoutWarning;
